import {
  oneWords,
  ROOT,
  textWords,
  type WizardFieldErrors,
  type WizardRefusalWording,
} from "@/base";
import { formatAddress } from "@/lib/address";
import type { FieldOption } from "@/lib/brokerProducts";
import type {
  SubmissionLocation,
  SubmissionPerson,
  SubmissionPersonInput,
} from "@/lib/submissions/brokerSubmissions";
import {
  CONTACT_FOR,
  contactInput,
  EMAIL_SHAPE,
  FIRST_NAME,
  isContact,
  LAST_NAME,
  phoneWords,
  validateContacts,
  type WcContact,
} from "@/lib/submissions/wc/contacts";
import {
  isContactRole,
  isOwnerOrOfficer,
  OFFICER_ROLES,
  ownerInput,
  OWNERSHIP_OVER_100,
  personAsInput,
  type WcPerson,
} from "@/lib/submissions/wc/people";

// The WC wizard's People step (BRK-S303, BRK-S391), as pure rules: the owners
// and officers, then the other contacts, written as one list of the
// submission's people in one PUT (PMS-S740), which PMS replaces whole. Tyler's
// doc, Step 3, and Nav (10/21–22):
// - at least one owner or officer; a contact alone doesn't count (PMS-S813's
//   `owners` floor key);
// - each with a title for the legal entity, sent as text, and the location
//   they work at, one with payroll;
// - each may be the inspection, accounting or claims contact, ticked on
//   their own row rather than entered twice.

/** The page's own key for its first check, as PMS names the floor key. */
export const OWNERS = "owners";
export const OWNERS_REQUIRED = "Add at least one owner or officer.";

/** The payroll rows' locations, whatever shape the bag holds. */
function payrollLocationIds(data: Record<string, unknown>): Set<string> {
  const rows = Array.isArray(data.exposures) ? data.exposures : [];
  const ids = new Set<string>();
  for (const row of rows) {
    const id =
      typeof row === "object" && row !== null
        ? (row as Record<string, unknown>).locationId
        : undefined;
    if (typeof id === "string") ids.add(id);
  }
  return ids;
}

/** Where an owner can work: the submission's locations with payroll, in its
 *  order. PMS refuses any other (PMS-S763). */
export function payrollLocations(
  saved: readonly SubmissionLocation[],
  data: Record<string, unknown>,
): SubmissionLocation[] {
  const ids = payrollLocationIds(data);
  return saved.filter((l) => ids.has(l.id));
}

/** Those locations as options, numbered as the Locations step numbers them,
 *  each by its full address. */
export function locationOptions(
  saved: readonly SubmissionLocation[],
  data: Record<string, unknown>,
): FieldOption[] {
  const ids = payrollLocationIds(data);
  return saved.flatMap((l, i) =>
    ids.has(l.id)
      ? [{ value: l.id, label: `Location ${i + 1} — ${formatAddress(l)}` }]
      : [],
  );
}

/**
 * The whole list the page writes, in PMS's order with new people last
 * (owners, then contacts), and where each row lands in it (`index`, by row
 * key), so a refusal keyed `personnel.N.<field>` finds its row.
 *
 * Everyone the page doesn't show is carried exactly as PMS holds them. A
 * removed owner who is also a contact stays, as a contact only; a removed
 * contact who holds another role keeps it; anyone else removed is left out,
 * so PMS unlinks them.
 */
export function peopleStepToWire(
  owners: readonly WcPerson[],
  contacts: readonly WcContact[],
  personnel: readonly SubmissionPerson[],
): { wire: SubmissionPersonInput[]; index: Record<string, number> } {
  const ownerById = new Map(owners.filter((r) => r.id).map((r) => [r.id, r]));
  const contactById = new Map(
    contacts.filter((r) => r.id).map((r) => [r.id, r]),
  );
  const wire: SubmissionPersonInput[] = [];
  const index: Record<string, number> = {};
  const add = (key: string, input: SubmissionPersonInput) => {
    index[key] = wire.length;
    wire.push(input);
  };
  for (const p of personnel) {
    const owner = ownerById.get(p.id);
    const contact = contactById.get(p.id);
    if (owner) add(owner.key, ownerInput(owner));
    else if (contact) add(contact.key, contactInput(contact, p));
    else if (isOwnerOrOfficer(p)) {
      const others = p.roles.filter((r) => !OFFICER_ROLES.includes(r));
      if (others.some(isContactRole)) {
        wire.push({
          ...personAsInput(p, false),
          officerTitle: null,
          roles: others,
        });
      }
    } else if (isContact(p)) {
      const others = p.roles.filter((r) => !isContactRole(r));
      if (others.length > 0) wire.push({ ...personAsInput(p), roles: others });
    } else {
      wire.push(personAsInput(p));
    }
  }
  for (const r of owners) if (!r.id) add(r.key, ownerInput(r));
  for (const r of contacts) if (!r.id) add(r.key, contactInput(r));
  return { wire, index };
}

// An owner's sentences, said alike by the checks and for PMS's (BRK-S339).
const TITLE = "Add the title.";
const LOCATION = "Choose the location.";
const INCLUSION = "Choose included or excluded.";
const SHARE = "Enter a percentage from 0 to 100.";
const PAYROLL = "Enter the payroll as $0 or more.";

/** What PMS's checks of the people write refuse, in the step's words
 *  (BRK-S339), for the owners and the other contacts alike: the one list
 *  `peopleStepToWire` sends, a row's index its place there. */
export const PEOPLE_REFUSALS: WizardRefusalWording = {
  "personnel.*.firstName": textWords(FIRST_NAME, "first name"),
  "personnel.*.lastName": textWords(LAST_NAME, "last name"),
  "personnel.*.title": textWords(TITLE, "title"),
  "personnel.*.email": textWords(EMAIL_SHAPE, "email address"),
  "personnel.*.phones.*.number": phoneWords("Enter the phone number."),
  "personnel.*.locationId": oneWords(LOCATION),
  "personnel.*.wcInclusion": oneWords(INCLUSION),
  "personnel.*.ownershipPercentage": oneWords(SHARE),
  "personnel.*.annualRemuneration": oneWords(PAYROLL),
  "personnel.*.roles": oneWords(CONTACT_FOR),
};

/**
 * The page's checks, keyed as PMS keys its refusals; {} when it may be saved.
 * At least one owner or officer; each with names, a title, the location they
 * work at (one of `locationIds`, the payroll locations), and included or
 * excluded; a share and payroll are optional. Then the other contacts. The
 * ownership total is PMS's account rule, checked in hundredths of a percent
 * so three thirds don't add up to 100.00000000000001.
 */
export function validatePeopleStep(
  owners: readonly WcPerson[],
  contacts: readonly WcContact[],
  personnel: readonly SubmissionPerson[],
  locationIds: readonly string[],
): WizardFieldErrors {
  const { wire, index } = peopleStepToWire(owners, contacts, personnel);
  const errors: Record<string, string> = {};
  if (owners.length === 0) errors[OWNERS] = OWNERS_REQUIRED;
  for (const r of owners) {
    const at = (field: string) => `personnel.${index[r.key]}.${field}`;
    if (!r.firstName.trim()) errors[at("firstName")] = FIRST_NAME;
    if (!r.lastName.trim()) errors[at("lastName")] = LAST_NAME;
    if (!r.title.trim()) errors[at("title")] = TITLE;
    if (!locationIds.includes(r.locationId)) {
      errors[at("locationId")] = LOCATION;
    }
    if (!r.wcInclusion) errors[at("wcInclusion")] = INCLUSION;
    const share = r.ownershipPercentage;
    if (share !== undefined && (share < 0 || share > 100)) {
      errors[at("ownershipPercentage")] = SHARE;
    }
    if (r.annualRemuneration !== undefined && r.annualRemuneration < 0) {
      errors[at("annualRemuneration")] = PAYROLL;
    }
  }
  Object.assign(errors, validateContacts(contacts, index));
  const hundredths = wire.reduce(
    (sum, p) => sum + Math.round((p.ownershipPercentage ?? 0) * 100),
    0,
  );
  if (hundredths > 100 * 100) errors[ROOT] = OWNERSHIP_OVER_100;
  return errors;
}
